import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ImagePlus, Loader2, X, FileText } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { ChatAttachment } from "@/components/ChatAttachment";
import { uploadFile, CHAT_MAX_BYTES, CHAT_ACCEPT } from "@/lib/chatUpload";

const MAX_FILES = 10;

export const RequestPhotosInput = ({ value, onChange, max = MAX_FILES, label = "أضف صور العطل أو ملف PDF", hint = "اختياري — الصور تساعد الورش على إعطائك سعراً أدق. حتى 25 ميجابايت للملف." }) => {
  const ref = useRef(null);
  const [progress, setProgress] = useState(null);

  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const room = max - value.length;
    if (files.length > room) toast.error(`الحد الأقصى ${max} مرفقات`);
    let list = [...value];
    for (const file of files.slice(0, room)) {
      if (file.size > CHAT_MAX_BYTES) { toast.error(`${file.name}: أكبر من 25 ميجابايت`); continue; }
      setProgress({ name: file.name, pct: 0 });
      try {
        const { attachment } = await uploadFile("/chat-files/draft/init", file, (pct) => setProgress({ name: file.name, pct }));
        list = [...list, attachment];
        onChange(list);
      } catch (ex) { toast.error(`${file.name}: ${formatApiError(ex.response?.data?.detail) || "تعذّر الرفع"}`); }
    }
    setProgress(null);
  };

  return (
    <div className="space-y-2" data-testid="req-photos">
      {value.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {value.map((a) => (
            <div key={a.file_id} className="relative rounded-xl border border-border p-1 bg-muted/30" data-testid={`req-photo-${a.file_id}`}>
              {a.content_type === "application/pdf" || a.content_type === "image/heic"
                ? <div className="h-20 flex flex-col items-center justify-center text-[10px] text-center gap-1 px-1"><FileText className="w-6 h-6" /><span className="truncate w-full">{a.name}</span></div>
                : <ChatAttachment att={a} compact />}
              <button type="button" onClick={() => onChange(value.filter((x) => x.file_id !== a.file_id))}
                className="absolute -top-2 -start-2 w-6 h-6 rounded-full bg-destructive text-white flex items-center justify-center" aria-label="إزالة" data-testid={`req-photo-remove-${a.file_id}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
      {progress && (
        <div className="text-xs flex items-center gap-2" data-testid="req-photos-progress">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> <span className="truncate max-w-[160px]">{progress.name}</span> {progress.pct}%
        </div>
      )}
      <input ref={ref} type="file" multiple accept={CHAT_ACCEPT} className="hidden" onChange={pick} data-testid="req-photos-input" />
      <Button type="button" variant="outline" className="w-full rounded-xl gap-2 border-dashed" disabled={!!progress || value.length >= max}
        onClick={() => ref.current?.click()} data-testid="req-photos-add-btn">
        <ImagePlus className="w-4 h-4" /> {label} ({value.length}/{max})
      </Button>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
};
